import React, { useEffect, useRef, useState } from "react";
import cnBind from "classnames/bind";
import styles from "./AboutUsBlock.module.scss";

const cx = cnBind.bind(styles);

const Counter = ({ end, duration = 1500, shouldStart }: { end: number; duration?: number; shouldStart: boolean }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (!shouldStart) return;
        let startTimestamp: number | null = null;
        const step = (timestamp: number) => {
            if (!startTimestamp) startTimestamp = timestamp;
            const progress = Math.min((timestamp - startTimestamp) / duration, 1);
            setCount(Math.floor(progress * end));
            if (progress < 1) window.requestAnimationFrame(step);
        };
        const delayTimeout = setTimeout(() => {
            window.requestAnimationFrame(step);
        }, 300);
        return () => clearTimeout(delayTimeout);
    }, [end, duration, shouldStart]);

    return <>{count.toLocaleString('ru-RU').replace(/\s/g, '\u00A0')}+</>;
};

export const AboutUsBlock = () => {
    const [isVisible, setIsVisible] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    // Останавливаем наблюдение после первого срабатывания
                    if (containerRef.current) {
                        observer.unobserve(containerRef.current);
                    }
                }
            },
            {
                threshold: 0.15 // Сработает, когда 15% блока будет в поле зрения
            }
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const items = [
        { id: 1, val: 17, suffix: "лет", desc: "опыта в рекламном бизнесе", icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
            )},
        { id: 2, val: 3189, suffix: "", desc: "постоянных клиентов", icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>
            )},
        { id: 3, val: 11278, suffix: "", desc: "рекламных поверхностей", icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
            )},
        { id: 4, val: 19839, suffix: "", desc: "реализованных проектов", icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
            )},
    ];

    return (
        <section className={cx("about-us")} ref={containerRef}>
            <div className={cx("wrapper")}>
                <div className={cx("header", { "show": isVisible })}>
                    <h2>
                        Эффективный клиентский сервис{'\u00A0'}—{'\u00A0'}наш приоритет
                    </h2>
                </div>
                <div className={cx("grid")}>
                    {items.map((item, i) => (
                        <div
                            key={item.id}
                            className={cx("card", { "show": isVisible })}
                            style={{ transitionDelay: `${i * 0.15}s` }}
                        >
                            <div className={cx("value-header")}>
                                <div className={cx("icon-box")}>{item.icon}</div>
                                <div className={cx("value")}>
                                    <strong><Counter end={item.val} shouldStart={isVisible} /></strong>
                                    {item.suffix && <span className={cx("suffix")}>{item.suffix}</span>}
                                </div>
                            </div>
                            <p>{item.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};
